import React, { useMemo, useRef } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { MathUtils, Vector3, Color } from 'three';
import * as THREE from 'three';

const vertexShader = `
uniform float u_intensity;
uniform float u_time;

varying vec2 vUv;
varying float vDisplacement;

float cnoise(vec3 P) {
    vec3 Pi0 = floor(P);
    vec3 Pi1 = Pi0 + vec3(1.0);
    Pi0 = mod(Pi0, 289.0);
    Pi1 = mod(Pi1, 289.0);
    vec3 Pf0 = fract(P);
    vec3 Pf1 = Pf0 - vec3(1.0);
    
    return 2.0 * fract(sin(dot(Pi0, vec2(12.9898,78.233))) * 43758.5453123);
}

void main() {
    vUv = uv;
    vDisplacement = cnoise(position + vec3(0.5 * u_time)) * 0.3;
    
    vec3 newPosition = position + normal * (u_intensity * vDisplacement);
    
    vec4 modelPosition = modelMatrix * vec4(newPosition, 1.0);
    vec4 viewPosition = viewMatrix * modelPosition;
    vec4 projectedPosition = projectionMatrix * viewPosition;
    
    gl_Position = projectedPosition;
}
`;

const fragmentShader = `
uniform float u_intensity;
uniform float u_time;
uniform vec3 u_color;

varying vec2 vUv;
varying float vDisplacement;

void main() {
  float distort = vDisplacement * u_intensity * 0.5;
  vec3 color = mix(u_color, vec3(0.9, 0.95, 1.0), distort + 0.3);
  
  gl_FragColor = vec4(color, 0.8);
}
`;

const RoleBlob: React.FC = () => {
  const mesh = useRef<THREE.Mesh>(null);

  const uniforms = useMemo(
    () => ({
      u_time: { value: 0 },
      u_intensity: { value: 0.2 },
      u_color: { value: new Color('#3b82f6') }, // Blue color
    }),
    [],
  );

  useFrame((state) => {
    const { clock } = state;

    if (mesh.current) {
      const material = mesh.current.material as THREE.ShaderMaterial;
      material.uniforms.u_time.value = 0.3 * clock.getElapsedTime();
    }
  });

  return (
    <mesh ref={mesh} scale={2} position={[0, 0, 0]}>
      <icosahedronGeometry args={[1.5, 8]} />
      <shaderMaterial vertexShader={vertexShader} fragmentShader={fragmentShader} uniforms={uniforms} transparent />
    </mesh>
  );
};

const RoleBackground: React.FC = () => {
  return (
    <div className="absolute inset-0 overflow-hidden rounded-2xl">
      <Canvas
        camera={{ position: [0, 0, 3], fov: 45 }}
        style={{ background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 20%, #bfdbfe 100%)' }}
      >
        <ambientLight intensity={0.4} />
        <RoleBlob />
      </Canvas>
    </div>
  );
};

export default RoleBackground;
